import { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { LabelValueRow } from '../LabelValueRow';
import { formatCurrency } from '../../../../shared/utils/currency';
import { useCancelAdminDeduction } from '../../hooks/useCancelAdminDeduction';

export interface CancelDeductionDialogDeduction {
  id: string;
  uniqueReference: string | null;
  description: string;
  amount: number;
  workerOrGangName?: string | null;
  status: string;
}

const statusChipConfig: Record<
  string,
  { color: 'success' | 'warning' | 'error' | 'default'; label: string }
> = {
  Approved: { color: 'success', label: 'Approved' },
  Booked: { color: 'warning', label: 'Booked' },
  Rejected: { color: 'error', label: 'Rejected' },
  Cancelled: { color: 'default', label: 'Cancelled' },
};

export function CancelDeductionDialog({
  open,
  onClose,
  deduction,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  deduction: CancelDeductionDialogDeduction;
  onSuccess: (message: string) => void;
}) {
  const [notes, setNotes] = useState('');

  const { mutate: cancelDeduction, isPending } = useCancelAdminDeduction();

  const notesTooShort = notes.trim().length > 0 && notes.trim().length < 10;

  const handleConfirm = () => {
    cancelDeduction(
      {
        deductionId: deduction.id,
        request: {
          cancellationNotes: notes.trim() || null,
        },
      },
      {
        onSuccess: () => onSuccess('Deduction cancelled successfully'),
      }
    );
  };

  const handleClose = () => {
    if (!isPending) {
      setNotes('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel Deduction</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Stack spacing={1}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {deduction.uniqueReference ?? '—'}
                </Typography>
                <Chip
                  label={
                    statusChipConfig[deduction.status]?.label ??
                    deduction.status
                  }
                  color={statusChipConfig[deduction.status]?.color ?? 'default'}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Typography variant="body2" fontWeight={600}>
                {deduction.description}
              </Typography>
              <LabelValueRow
                label="Worker / Gang"
                value={deduction.workerOrGangName ?? '—'}
              />
              <LabelValueRow
                label="Amount"
                value={formatCurrency(deduction.amount)}
              />
            </Stack>
          </Box>

          <TextField
            label="Cancellation Notes (optional)"
            multiline
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Add any notes about this cancellation..."
            fullWidth
            error={notesTooShort}
            helperText={
              notesTooShort ? 'Min 10 characters if provided' : undefined
            }
            slotProps={{
              htmlInput: {
                'data-testid': 'cancel-deduction-notes',
              },
            }}
          />

          <Alert severity="warning">
            Cancelling this deduction will update pot financial totals. This
            action cannot be undone.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined" disabled={isPending}>
          Go Back
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isPending || notesTooShort}
          startIcon={
            isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CancelIcon />
            )
          }
          data-testid="confirm-cancel-deduction-button"
        >
          {isPending ? 'Cancelling...' : 'Confirm Cancellation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
